import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

export interface BreadcrumbItem {
  key: string;
  title: React.ReactNode;
  path?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  clearBreadcrumbs: () => void;
  addBreadcrumb: (breadcrumb: BreadcrumbItem) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

interface BreadcrumbProviderProps {
  children: ReactNode;
}

export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  // Memoized so their identity is stable across renders — consumers put these
  // in useEffect dependency arrays (e.g. ProductDetail), and unstable refs here
  // caused an infinite setState→re-render loop ("Maximum update depth exceeded").
  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbs([]);
  }, []);

  const addBreadcrumb = useCallback((breadcrumb: BreadcrumbItem) => {
    setBreadcrumbs(prev => [...prev, breadcrumb]);
  }, []);

  const value = useMemo(() => ({
    breadcrumbs,
    setBreadcrumbs,
    clearBreadcrumbs,
    addBreadcrumb,
  }), [breadcrumbs, clearBreadcrumbs, addBreadcrumb]);

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumb = (): BreadcrumbContextType => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
};

export default BreadcrumbContext;